import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement } from 'chart.js';

ChartJS.register(ArcElement);

// Colores por opción (A, B, C, D). Vivos y distinguibles en stream.
const COLORES = ['#3b82f6', '#f59e0b', '#10b981', '#ec4899'];
const IDENTIFICADORES = ['A', 'B', 'C', 'D'];

// Extrae los porcentajes (0-100) del payload del overlay de forma robusta.
const extraerPorcentajes = (datos) => {
  const bruto = datos?.datasets?.[0]?.data;
  const base = Array.isArray(bruto) ? bruto : [];
  const norm = [];
  for (let i = 0; i < 4; i += 1) {
    const n = Number(base[i]);
    norm.push(Number.isFinite(n) && n > 0 ? n : 0);
  }
  return norm;
};

// Plugin: dibuja el porcentaje encima de cada sector (solo si > 0).
const etiquetasPorcentaje = {
  id: 'etiquetasPorcentaje',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const valores = chart.data.datasets[0].data;
    meta.data.forEach((arco, i) => {
      const valor = valores[i];
      if (!valor) return;
      const { x, y } = arco.tooltipPosition();
      ctx.save();
      ctx.font = '800 20px system-ui, sans-serif';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.9)';
      ctx.shadowBlur = 5;
      ctx.fillText(`${Math.round(valor)}%`, x, y);
      ctx.restore();
    });
  },
};

// Plugin: dibuja los segundos restantes del comodín en el centro del círculo.
// El valor llega por options.plugins.tiempoCentral.valor (segundos). Si no hay
// un número válido, no pinta nada (deja el hueco del donut limpio).
const tiempoCentral = {
  id: 'tiempoCentral',
  afterDatasetsDraw(chart, _args, opts) {
    const valor = opts?.valor;
    if (valor === null || valor === undefined || !Number.isFinite(Number(valor))) return;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const cx = (chartArea.left + chartArea.right) / 2;
    const cy = (chartArea.top + chartArea.bottom) / 2;
    const segundos = Math.max(0, Math.round(Number(valor)));

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.9)';
    ctx.shadowBlur = 6;
    // Rojo cuando queda poco (<=10s), como el temporizador de la llamada.
    ctx.fillStyle = segundos <= 10 ? '#f87171' : '#fff';
    ctx.font = '900 48px system-ui, sans-serif';
    ctx.fillText(String(segundos), cx, cy - 8);
    ctx.font = '800 18px system-ui, sans-serif';
    ctx.fillText('seg', cx, cy + 26);
    ctx.restore();
  },
};

const ChartPublicoOBS = ({ datos, tiempo }) => {
  const porcentajes = extraerPorcentajes(datos);
  const hayVotos = porcentajes.some((p) => p > 0);

  const data = {
    labels: IDENTIFICADORES,
    datasets: [
      {
        // Si nadie ha votado, un anillo gris tenue para no dejarlo vacío.
        data: hayVotos ? porcentajes : [1, 1, 1, 1],
        backgroundColor: hayVotos ? COLORES : COLORES.map(() => 'rgba(148,163,184,0.35)'),
        borderColor: 'rgba(15,23,42,0.55)',
        borderWidth: 3,
        hoverOffset: 0,
      },
    ],
  };

  const opciones = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '58%',
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      tiempoCentral: { valor: tiempo },
    },
    animation: { duration: 250 },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 240, height: 240, filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.55))' }}>
        <Doughnut data={data} options={opciones} plugins={[etiquetasPorcentaje, tiempoCentral]} />
      </div>

      {/* Porcentajes de cada respuesta */}
      <div style={{ display: 'flex', gap: 22, justifyContent: 'center', flexWrap: 'wrap' }}>
        {IDENTIFICADORES.map((letra, i) => (
          <div key={letra} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: '9999px',
                background: COLORES[i],
                boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontWeight: 800,
                fontSize: 22,
                color: '#fff',
                textShadow: '0 2px 6px rgba(0,0,0,0.85)',
                lineHeight: 1,
              }}
            >
              {letra}) {Math.round(porcentajes[i])}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChartPublicoOBS;
